import { useEffect, useRef, useState, type ChangeEvent, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api.ts';
import { useDictation } from '../lib/dictation.ts';
import { useImageDrop } from '../lib/drop.ts';
import { AgentLogo, Icon } from '../lib/icons.tsx';
import { MAX_CHAT_IMAGES } from '../lib/images.ts';
import {
  AGENTS,
  EFFORTS,
  agentForModel,
  effortsFor,
  enabledModelsForAgent,
  modelsForAgent,
  nextVisibleAgent,
  type CatalogModelId,
  type EffortId,
  type ModelId,
} from '../lib/models.ts';
import { useEnabledModelIds } from '../lib/model-catalog.ts';
import { SKIP_VERIFICATION_NOTE, type ChatImage } from '../lib/protocol.ts';
import { Markdown } from '../lib/markdown.tsx';
import { ChoiceGroup } from './ChoiceGroup.tsx';

/**
 * Tighter names for the task bar, where the buttons share one row. Claude rows
 * keep their version - a bare `Opus` would hide which model is about to run.
 */
const SHORT_LABELS: Record<CatalogModelId, string> = {
  'composer-2.5': 'Composer',
  'cursor-grok-4.6': 'Grok',
  'claude-opus-5': 'Opus 5',
  'claude-code-opus': 'Opus 5.5',
  'claude-code-sonnet': 'Sonnet 5',
  'claude-code-haiku': 'Haiku 4.5',
  'grok-4.6': '4.6',
  'opencode-grok-4.6': 'Grok',
  'opencode-glm-5.3-flash': 'GLM Flash',
  'opencode-qwen3.8-flash': 'Qwen Flash',
  'opencode-glm-5.3': 'GLM-5.3',
  'openai-gpt-6-astra': 'Astra',
  'openai-gpt-6-sol': 'Sol 6',
  'openai-gpt-6-luna': 'Luna 6',
  'openai-gpt-5.6-sol': 'Sol 5.6',
  'openai-gpt-5.6-terra': 'Terra',
  'openai-gpt-5.6-luna': 'Luna 5.6',
};

export function TaskView({
  task,
  title,
  taskImages = [],
  sessionId,
  hidden,
  model,
  effort,
  onModel,
  onEffort,
  onSave,
  onDropImages,
  onEditingChange,
  skipVerification = false,
  onSkipVerification,
  dock,
}: {
  task: string;
  title: string;
  taskImages?: ChatImage[];
  sessionId: string;
  hidden?: boolean;
  model: ModelId;
  effort: EffortId | null;
  onModel: (model: ModelId) => void;
  onEffort: (effort: EffortId) => void;
  onSave: (text: string, title: string) => Promise<boolean>;
  onDropImages?: (files: File[]) => void | Promise<void>;
  onEditingChange?: (editing: boolean) => void;
  skipVerification?: boolean;
  onSkipVerification?: (skip: boolean) => void;
  dock?: HTMLElement | null;
}): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [titleDraft, setTitleDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [addingImages, setAddingImages] = useState(false);
  const editorRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastByAgent = useRef<Partial<Record<(typeof AGENTS)[number]['id'], ModelId>>>({
    [agentForModel(model)]: model,
  });

  const dictation = useDictation((phrase) => {
    setDraft((prev) => (prev === '' || /\s$/.test(prev) ? prev + phrase : `${prev} ${phrase}`));
  });

  const body = task.trim();
  const agentId = agentForModel(model);
  const agent = AGENTS.find((a) => a.id === agentId)!;
  const nextAgent = nextVisibleAgent(agentId);
  const rungs = effortsFor(model).map((id) => EFFORTS.find((e) => e.id === id)!);
  const enabledIds = useEnabledModelIds();
  const allowed = enabledModelsForAgent(agentId, enabledIds.length > 0 ? enabledIds : null);
  // The task's own model stays on the bar even when More → Models turned it off.
  const modelOptions = allowed.some((m) => m.id === model)
    ? allowed
    : [...modelsForAgent(agentId).filter((m) => m.id === model), ...allowed];
  const hasContent = body.length > 0 || taskImages.length > 0;
  const atImageCap = taskImages.length >= MAX_CHAT_IMAGES;

  const setEditingOn = (next: boolean): void => {
    if (!next) dictation.cancel();
    setEditing(next);
    onEditingChange?.(next);
  };

  useEffect(() => {
    setEditingOn(false);
    setDraft('');
    setTitleDraft('');
    setError(null);
  }, [sessionId]);

  useEffect(() => {
    if (hidden) setEditingOn(false);
  }, [hidden]);

  useEffect(() => {
    lastByAgent.current[agentForModel(model)] = model;
  }, [model]);

  useEffect(() => {
    if (!editing) return;
    const el = editorRef.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, [editing]);

  const toggleAgent = (): void => {
    const remembered = lastByAgent.current[nextAgent.id];
    onModel(
      remembered && agentForModel(remembered) === nextAgent.id
        ? remembered
        : modelsForAgent(nextAgent.id)[0]!.id,
    );
  };

  const startEdit = (): void => {
    setDraft(task);
    setTitleDraft(title);
    setError(null);
    setEditingOn(true);
  };

  const cancelEdit = (): void => {
    setEditingOn(false);
    setDraft('');
    setTitleDraft('');
    setError(null);
  };

  const saveEdit = async (): Promise<void> => {
    if (saving) return;
    const nextTitle = titleDraft.trim();
    if (!nextTitle) {
      setError('Enter a task title.');
      return;
    }
    const unchanged = draft === task && nextTitle === title;
    if (unchanged) {
      setEditingOn(false);
      return;
    }
    setSaving(true);
    try {
      if (await onSave(draft, nextTitle)) {
        setEditingOn(false);
        setDraft('');
        setTitleDraft('');
        setError(null);
      } else {
        setError('Could not save the task.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const addFiles = async (files: File[]): Promise<void> => {
    if (!onDropImages || files.length === 0) return;
    setAddingImages(true);
    setError(null);
    try {
      await onDropImages(files);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setAddingImages(false);
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>): void => {
    const files = [...(e.target.files ?? [])];
    e.target.value = '';
    void addFiles(files);
  };

  const drop = useImageDrop((files) => void addFiles(files), Boolean(onDropImages) && !hidden);

  const modelBar = !editing && (
    <div className="task-model-bar">
      <ChoiceGroup
        label={`${agent.label} models`}
        value={model}
        options={modelOptions.map((m) => ({
          id: m.id,
          label: SHORT_LABELS[m.id as CatalogModelId] ?? m.label,
        }))}
        onChange={onModel}
      />
      {rungs.length > 0 && effort && (
        <ChoiceGroup label="Effort" value={effort} options={rungs} onChange={onEffort} />
      )}
    </div>
  );

  const note = error || dictation.error;

  return (
    <div
      className={`task-pane${editing ? ' task-editing' : ''}${drop.over ? ' is-drop-target' : ''}`}
      id="pane-task"
      role="tabpanel"
      aria-labelledby="tab-task"
      hidden={hidden}
      {...drop.handlers}
    >
      <div className="task-scroll">
        {note && <div className="note note-error">{note}</div>}
        {editing && dictation.listening && (
          <div className="task-interim" role="status">
            <span className="rec-dot" aria-hidden="true" />
            <span className="task-interim-text">{dictation.interim || 'Listening...'}</span>
          </div>
        )}
        {editing ? (
          <>
            {taskImages.length > 0 && (
              <div className="bubble-images task-images">
                {taskImages.map((img) => (
                  <img
                    key={img.id}
                    src={api.imageUrl(sessionId, img.id)}
                    alt=""
                    className="bubble-image"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
            <label className="task-title-field">
              <span className="task-title-label">Task title</span>
              <input
                className="task-title-input"
                value={titleDraft}
                onChange={(e) => setTitleDraft(e.target.value)}
                placeholder="Task title"
                aria-label="Task title"
                autoCapitalize="sentences"
                autoCorrect="on"
                spellCheck
              />
            </label>
            <textarea
              ref={editorRef}
              className="task-editor"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Write the task"
              aria-label="Task prompt"
              autoCapitalize="sentences"
              autoCorrect="on"
              spellCheck
            />
          </>
        ) : hasContent ? (
          <div className="task-body">
            {taskImages.length > 0 && (
              <div className="bubble-images task-images">
                {taskImages.map((img) => (
                  <img
                    key={img.id}
                    src={api.imageUrl(sessionId, img.id)}
                    alt=""
                    className="bubble-image"
                    loading="lazy"
                  />
                ))}
              </div>
            )}
            {body ? <Markdown text={task} /> : null}
            {skipVerification && (
              <p className="task-skip-note">{SKIP_VERIFICATION_NOTE}</p>
            )}
          </div>
        ) : (
          <div className="empty">
            <p>No task yet.</p>
            <p className="empty-hint">
              Tap edit to write a prompt. It stays here until you run it from Home. A successful run
              commits the working tree.
            </p>
            {skipVerification && (
              <p className="task-skip-note">{SKIP_VERIFICATION_NOTE}</p>
            )}
          </div>
        )}
      </div>
      {editing ? (
        <div className="fabs">
          {dictation.available && (
            <button
              type="button"
              className={`fab fab-quiet${dictation.listening || dictation.stopping ? ' fab-listening' : ''}`}
              onClick={dictation.toggle}
              disabled={dictation.stopping}
              aria-label={dictation.listening ? 'Stop dictation' : 'Dictate'}
              aria-pressed={dictation.listening}
            >
              <Icon name="mic" size={22} strokeWidth={2.4} />
            </button>
          )}
          <button
            type="button"
            className="fab fab-quiet"
            onClick={cancelEdit}
            aria-label="Cancel editing"
          >
            <Icon name="close" size={22} strokeWidth={2.4} />
          </button>
          <button
            type="button"
            className="fab"
            onClick={() => void saveEdit()}
            disabled={saving}
            aria-label="Save task"
          >
            <Icon name="check" size={22} strokeWidth={2.4} />
          </button>
        </div>
      ) : (
        <div className="fabs">
          {onDropImages && (
            <button
              type="button"
              className="fab fab-quiet"
              onClick={() => fileRef.current?.click()}
              disabled={atImageCap || addingImages}
              aria-label="Add photo"
            >
              <Icon name="image" size={22} strokeWidth={2.4} />
            </button>
          )}
          {onSkipVerification && (
            <button
              type="button"
              className={`fab fab-quiet${skipVerification ? ' fab-open' : ''}`}
              onClick={() => onSkipVerification(!skipVerification)}
              aria-label="Skip verification"
              aria-pressed={skipVerification}
              title={SKIP_VERIFICATION_NOTE}
            >
              <Icon name="flaskOff" size={22} strokeWidth={2.4} />
            </button>
          )}
          <button
            type="button"
            className="fab fab-quiet"
            onClick={toggleAgent}
            aria-label={`Switch to ${nextAgent.label}`}
            title={agent.label}
          >
            <AgentLogo agent={agentId} size={20} />
          </button>
          <button type="button" className="fab" onClick={startEdit} aria-label="Edit task">
            <Icon name="pencil" size={22} strokeWidth={2.4} />
          </button>
        </div>
      )}
      {onDropImages && (
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={onPick}
        />
      )}
      {dock ? createPortal(modelBar, dock) : modelBar}
      {drop.over && (
        <div className="drop-overlay" aria-hidden="true">
          Drop photos
        </div>
      )}
    </div>
  );
}
